/**
 * Everything the reply composer decides, kept out of the card that draws it.
 *
 * The card is the reference's AI Draft card under the last message, with a
 * formatted editor in it on email and a plain box everywhere else, and almost
 * none of what makes it correct is visual. What it opens with, what a press
 * sends on which channel, whether Send is allowed to fire, how many calls one
 * press takes, what the two send outcomes mean and what a reminder is honestly
 * allowed to claim are all rules, and there is no DOM test environment in this
 * workspace, so a rule that lives in the component is a rule no test can reach.
 * `transcript.ts`, `editor.ts`, `recipients.ts` and `templates.ts` beside this
 * file exist for the same reason and have the same shape.
 *
 * Three of these are worth reading before changing anything:
 *
 *   1. **A press of Send is one reply, however many calls it takes.** A draft
 *      the platform wrote already has an id and Send is one call. A reply
 *      somebody typed into an empty card has no draft behind it, so Send has to
 *      create one and then hand that to the dispatcher. If the second call
 *      fails, the first has still happened, and a card that forgets the id it
 *      was just given turns the next press into a second draft. `nextSendStep`
 *      is the whole of that: it is asked what to do given what the card now
 *      holds, never told.
 *   2. **`sending` is not `approved`.** The dispatcher reserves the allowance
 *      at hand-off, so a queued reply has already spent it and nothing is on
 *      the thread. Pressing Send again would reserve twice and put two copies of
 *      one reply in front of the same person. The server refuses that with a
 *      409, and this module refuses it before the request, so the button is out
 *      rather than lit-and-then-sorry.
 *   3. **A reminder promises nothing.** There is no job behind `remind_at` and
 *      no alarm anywhere in this product. It moves the thread onto a tab. The
 *      copy here says that in as many words, because "remind me" is a sentence
 *      every other product on somebody's screen backs with a notification.
 */
import { richTextToPlain, sanitizeRichText } from '@gojibasel/shared/rich-text'
import { countAgainstLimit } from '../../approvals/approvals-model'
import { plural } from '../../../lib/format'
import { METERED_ACTIONS_BY_CHANNEL, label } from '../../../lib/labels'
import type { ActionType, Channel, Conversation, ReplyDraftAuthor, ReplyDraftState } from '../../../lib/types'

// ---------------------------------------------------------------------------
// What the card opens with
// ---------------------------------------------------------------------------

export interface ComposerSeed {
  /** The draft this card is bound to, or null when nothing has been written yet. */
  draftId: string | null
  /** Where that draft has got to. Null when there is no draft. */
  state: ReplyDraftState | null
  /** The plain words: what the plain box opens with, and the email's text part. */
  body: string
  /**
   * The formatted version a draft was saved with, on email, or null. Every
   * draft written before formatting existed is plain, and opens in the email
   * editor through `plainToRichText` (`editor.ts`).
   */
  html: string | null
  /** The copies the draft was saved with. Email only. */
  cc: string[]
  bcc: string[]
  /** Whether the earlier messages go under the reply as a quote. Email only. */
  includeHistory: boolean
  /**
   * True when the words came from the model rather than from a person, which is
   * the only thing that entitles the card to call itself an AI draft. Read from
   * the draft's own `authoredBy`, never inferred from the draft existing: a
   * reply somebody typed and saved is a draft too, and on the scratch copy every
   * reply draft on file was one of those.
   */
  generated: boolean
}

/**
 * "Include thread history" starts ticked, as the reference's box does. The
 * server builds the quote from the stored messages, never from text the page
 * sends, so ticking it cannot put words in the thread that were not said.
 */
export const INCLUDE_HISTORY_DEFAULT = true

/** An empty card, for a thread with nothing drafted on it. */
export const EMPTY_SEED: ComposerSeed = {
  draftId: null,
  state: null,
  body: '',
  html: null,
  cc: [],
  bcc: [],
  includeHistory: INCLUDE_HISTORY_DEFAULT,
  generated: false,
}

/**
 * Whether a draft's words are the model's.
 *
 * Only an explicit 'model' counts. The server defaults rows written before the
 * field existed to 'model' itself, so anything else arriving here is a person's
 * words or a payload this build does not understand, and neither of those is
 * something to put the platform's name on.
 */
export function isModelWritten(draft: { authoredBy?: ReplyDraftAuthor | null }): boolean {
  return draft.authoredBy === 'model'
}

/**
 * What the composer opens with for one thread.
 *
 * `draftReply` has ridden on this payload since the inbox was built and no
 * screen has ever drawn it, so every reply this platform wrote landed in a list
 * nothing showed and a person answering the thread retyped it from scratch.
 * Seeding from it is the whole fix and it costs one function.
 *
 * A sent draft seeds nothing. Its words are already on the thread as an outbound
 * message, so putting them back in the box would invite somebody to send the
 * same reply twice, and the transcript above the card is the better place to
 * read them.
 */
export function composerSeed(conversation: Pick<Conversation, 'draftReply'>): ComposerSeed {
  const draft = conversation.draftReply ?? null
  if (draft === null || draft.state === 'sent') return EMPTY_SEED
  return {
    draftId: draft.id,
    state: draft.state,
    body: draft.body,
    // Each read with a fallback, because a server that has not learned the
    // fields yet sends none of them, and that has to read as a plain draft with
    // no copies rather than as an error.
    html: typeof draft.html === 'string' && draft.html.trim() !== '' ? draft.html : null,
    cc: Array.isArray(draft.cc) ? draft.cc : [],
    bcc: Array.isArray(draft.bcc) ? draft.bcc : [],
    includeHistory: typeof draft.includeHistory === 'boolean' ? draft.includeHistory : INCLUDE_HISTORY_DEFAULT,
    generated: isModelWritten(draft),
  }
}

// ---------------------------------------------------------------------------
// What a press sends
// ---------------------------------------------------------------------------

/**
 * Formatting, copies and the quoted thread are email's, and only email's.
 *
 * The server refuses `html`, `cc`, `bcc` and `includeHistory` with a 400 on any
 * other channel, and it is right to: a LinkedIn message has no copy line and
 * no markup. So the card draws those controls on email alone, and the payload
 * below never carries them anywhere else.
 */
export function isFormattedChannel(channel: Channel): boolean {
  return channel === 'email'
}

/** The request body every draft and send route takes, beside the ids. */
export interface ReplyPayload {
  body: string
  html?: string
  cc?: string[]
  bcc?: string[]
  includeHistory?: boolean
}

/**
 * What one press sends, from what the card holds.
 *
 * On email the editor's HTML goes through `sanitizeRichText` on its way out,
 * whatever the editor did to it, and the words are its plain twin, which is
 * also what the server stores as the body. The copies go as they stand, empty
 * lists included, so removing the last one from a saved draft clears it rather
 * than leaving the old list on file. On every other channel the plain words go
 * and nothing else, so an older card state can never put an email field on a
 * LinkedIn reply.
 */
/**
 * The payload as a request body: the words always, and each email field only
 * when the payload carries it, so a plain reply's request has no key a server
 * on another channel would refuse.
 */
export function replyRequestBody(payload: Partial<ReplyPayload>): Partial<ReplyPayload> {
  return {
    ...(payload.body === undefined ? {} : { body: payload.body }),
    ...(payload.html === undefined ? {} : { html: payload.html }),
    ...(payload.cc === undefined ? {} : { cc: payload.cc }),
    ...(payload.bcc === undefined ? {} : { bcc: payload.bcc }),
    ...(payload.includeHistory === undefined ? {} : { includeHistory: payload.includeHistory }),
  }
}

export function replyPayload(input: {
  channel: Channel
  body: string
  html: string | null
  cc: readonly string[]
  bcc: readonly string[]
  includeHistory: boolean
}): ReplyPayload {
  if (!isFormattedChannel(input.channel)) return { body: input.body.trim() }
  const html = sanitizeRichText(input.html ?? '')
  return {
    body: richTextToPlain(html).trim(),
    html,
    cc: [...input.cc],
    bcc: [...input.bcc],
    includeHistory: input.includeHistory,
  }
}

// ---------------------------------------------------------------------------
// What the card is called
// ---------------------------------------------------------------------------

/**
 * The heading, which is a claim about who wrote the words underneath it.
 *
 * The design calls the panel "AI Draft", spelled that way, and it is right about
 * the panel it was drawn from, where there is always a generated draft in it.
 * Here the same panel is also where a person writes a reply by hand or reopens
 * one they saved, and calling that an AI draft puts the platform's name on
 * somebody's own words. So the heading follows the draft's recorded author:
 * "AI Draft" for the model's words, "Reply" for a person's and for an empty card.
 */
export const AI_DRAFT_TITLE = 'AI Draft'
export const OWN_REPLY_TITLE = 'Reply'

export function composerTitle(seed: Pick<ComposerSeed, 'generated'>): string {
  return seed.generated ? AI_DRAFT_TITLE : OWN_REPLY_TITLE
}

/** The words of a box, compared loosely, so an editor's own spacing is not an edit. */
export function sameWords(a: string, b: string): boolean {
  return a.replace(/\s+/g, ' ').trim() === b.replace(/\s+/g, ' ').trim()
}

/**
 * The Draft with AI button, decided away from the card.
 *
 * A written draft becomes the thread's newest and the card opens it, so the
 * button only works while the box holds nothing, or a stored draft nobody has
 * changed since it opened: anything typed and not saved would be replaced. The
 * title says how to get it back rather than leaving a grey button unexplained.
 */
export function aiDraftButton(input: { locked: boolean; source: boolean; untouched: boolean; pending: boolean }): {
  label: string
  disabled: boolean
  title: string
} {
  if (input.pending) return { label: 'Writing a draft', disabled: true, title: 'The draft is being written. It opens here when it is done.' }
  const label = 'Draft with AI'
  if (input.locked) return { label, disabled: true, title: 'This reply is already on its way.' }
  if (input.source) return { label, disabled: true, title: 'Leave the source view first.' }
  if (!input.untouched) {
    return { label, disabled: true, title: 'Save or clear what you typed first. A written draft would replace it.' }
  }
  return {
    label,
    disabled: false,
    title: 'Writes the next message from the whole thread, as a draft for you to change and send. Nothing goes out.',
  }
}

/**
 * Where a draft has got to, in words, said under the heading.
 *
 * The two live states are the ones worth spelling out. "Waiting" is safe and
 * reversible. "On its way" is neither: the allowance is spent, a phone or a
 * mailbox has the work, and the only thing anybody can do about it is wait.
 *
 * The pending line is the one that names an author, so it has two spellings. A
 * draft a person saved says nothing about who wrote it, because the person
 * reading it most likely did, and "written by the platform" over their own
 * words is the same false claim the heading used to make.
 */
export const DRAFT_STATE_COPY: Record<ReplyDraftState, string> = {
  pending: 'Written by the platform and waiting for you. Nothing has gone out.',
  approved: 'Approved and still here. It goes out when you press Send.',
  sending:
    'On its way. It is with the sending account, nothing is on the thread yet, and it does not need pressing again.',
  sent: 'This one has gone out. It is on the thread above.',
}

export const HUMAN_PENDING_COPY = 'Saved and waiting for you. Nothing has gone out.'

export function draftStateCopy(
  state: ReplyDraftState | null,
  generated: boolean = true,
): string | null {
  if (state === null) return null
  if (state === 'pending' && !generated) return HUMAN_PENDING_COPY
  return DRAFT_STATE_COPY[state]
}

// ---------------------------------------------------------------------------
// What the arrow beside Send offers
// ---------------------------------------------------------------------------

/**
 * The fixed reason filed when a draft is discarded from the card.
 *
 * The discard route requires a reason and the card never sent one, so every
 * press of "Discard this draft" came back 400 with `reason: Required`, measured
 * on the scratch copy. A box asking why would turn a tidy-up into a form, and the
 * audit row only needs to say where the draft was dropped from.
 */
export const DISCARD_REASON = 'Discarded from the thread'

export interface DraftTrayActions {
  /** "Save draft" or "Approve without sending". */
  save: boolean
  /** "Log as sent": the person sent it themselves, somewhere this platform cannot reach. */
  markSent: boolean
  /** "Discard draft". */
  discard: boolean
  /** A sentence to draw in place of the actions that are gone, or null. */
  note: string | null
}

export const SENDING_TRAY_NOTE =
  'This reply is on its way, so there is nothing to approve or discard. It stays here until the sending account reports back.'

/**
 * Which of the arrow's actions a draft in this state can take.
 *
 * A 'sending' draft gets none of them, and they are taken away rather than
 * greyed out. On the scratch copy a sending draft could be discarded (200) and
 * re-approved (200, overwriting the stored body), and a discard followed by a
 * fresh draft reached the dispatcher a second time for the same person. The
 * server is closing that too, but a button offered on a reply that is already
 * with a phone invites exactly the press that duplicates it. A greyed button
 * still reads as "not yet", so the card says what is true instead.
 *
 * LOGGING AS SENT NO LONGER WAITS FOR AN APPROVED DRAFT. The route behind it
 * only takes an approved one, and the old tray made a person press three
 * things in order (save, approve, then "I sent this one by hand") with a line
 * explaining the order. `nextLogStep` below walks those steps for one press,
 * the way Send walks create-then-send, so the action is offered whenever
 * there is something that could have been sent.
 */
export function draftTrayActions(input: {
  draftId: string | null
  state: ReplyDraftState | null
}): DraftTrayActions {
  if (input.state === 'sending') {
    return { save: false, markSent: false, discard: false, note: SENDING_TRAY_NOTE }
  }
  if (input.state === 'sent') return { save: false, markSent: false, discard: false, note: null }
  return {
    save: true,
    markSent: true,
    discard: input.draftId !== null,
    note: null,
  }
}

/**
 * What one press of "Log as sent" does next, given what the card holds.
 *
 * The mark-sent route takes an approved draft and nothing else, so a press on
 * an empty card creates the draft, approves it with the words in the box, then
 * marks it sent, and a press on a waiting draft approves then marks. Asked
 * again after every step rather than planned once, for the reason
 * `nextSendStep` gives: a refusal half way leaves the card bound to the draft
 * that now exists, and the next press carries on from there instead of
 * writing a second draft.
 *
 * Approving here is not a decision taken for the person. They are saying they
 * sent these words, which is the approval, and the route that approves asks
 * the same brand-and-account question the mark-sent route asks.
 */
export type LogStep =
  | { kind: 'create' }
  | { kind: 'approve'; draftId: string }
  | { kind: 'mark'; draftId: string }
  | { kind: 'blocked'; reason: string }

export function nextLogStep(input: {
  draftId: string | null
  state: ReplyDraftState | null
  body: string
  /** Something the card holds that the routes would refuse, such as a copy that is not an address. */
  problem?: string | null
}): LogStep {
  if (input.state === 'sent') return { kind: 'blocked', reason: ALREADY_SENT }
  if (input.state === 'sending') return { kind: 'blocked', reason: ALREADY_SENDING }
  if (input.problem !== undefined && input.problem !== null) return { kind: 'blocked', reason: input.problem }
  if (input.body.trim() === '') return { kind: 'blocked', reason: NOTHING_WRITTEN }
  if (input.draftId === null) return { kind: 'create' }
  if (input.state === 'approved') return { kind: 'mark', draftId: input.draftId }
  return { kind: 'approve', draftId: input.draftId }
}

// ---------------------------------------------------------------------------
// How long it is
// ---------------------------------------------------------------------------

export interface ComposerCount {
  /** Words, the way somebody writing prose counts them. */
  words: number
  /** Characters the way a limit would count them, which is not the same number. */
  characters: number
  /** Both numbers, the count's tooltip. */
  line: string
  /** The words alone, "51 words", which is what the reference prints under the box. */
  short: string
}

/**
 * How many words this is.
 *
 * Whitespace runs, which is the count every word processor shows and the one the
 * design asks for. Deliberately not a linguistic count: a hyphenated name is one
 * word here and an em dash between two words would be none, and neither matters,
 * because nothing in this product acts on the number.
 */
export function countWords(text: string): number {
  const trimmed = text.trim()
  return trimmed === '' ? 0 : trimmed.split(/\s+/).length
}

/**
 * The counter under the box, and WHY IT SAYS BOTH NUMBERS.
 *
 * The design shows words. The approvals editor beside this one shows characters,
 * and there it is a gate rather than decoration: a draft over the platform's
 * body budget is refused at approval, so that counter exists to predict the
 * refusal and counts with `text.length` for exactly that reason.
 *
 * MEASURED ON THIS PATH, 2026-09-13: a reply draft is not gated on length at
 * all. `POST /replies/drafts/:id/send` hands the body to the dispatcher and
 * never calls `validateSendable`, which is the only thing in the product that
 * enforces a body budget, and it is reached from the approval queue and from the
 * public copy route and from nowhere else. So a word count here refuses nothing,
 * and a character count here refuses nothing either.
 *
 * Given that, the honest counter shows the number the design asked for and the
 * number a limit would count, and claims nothing about either. Showing only
 * words would hide the one that a platform ceiling would ever be measured in.
 * Showing only characters, styled like the gate next door, would imply a refusal
 * that cannot happen on this route. `countAgainstLimit` is imported rather than
 * reimplemented so that on the day a reply does get a budget, the number on this
 * screen is already the number that would refuse it.
 *
 * The card prints `short`, the reference's "51 words", and carries `line` with
 * both numbers in its tooltip, so neither number is lost and the corner reads
 * the way the owner's screenshot does.
 */
export function composerCount(text: string): ComposerCount {
  const words = countWords(text)
  const characters = countAgainstLimit(text)
  return {
    words,
    characters,
    line: `${plural(words, 'word')}, ${plural(characters, 'character')}`,
    short: plural(words, 'word'),
  }
}

// ---------------------------------------------------------------------------
// Pressing Send
// ---------------------------------------------------------------------------

export type SendStep =
  /** No draft behind these words yet. Create one, then send that. */
  | { kind: 'create' }
  /** There is a draft. Hand it to the dispatcher with whatever is in the box. */
  | { kind: 'send'; draftId: string }
  /** Nothing should be attempted, and this is the sentence saying why. */
  | { kind: 'blocked'; reason: string }

export const NOTHING_WRITTEN = 'There is nothing to send yet. Write the reply first.'
export const ALREADY_SENT = 'This reply has already gone out.'
export const ALREADY_SENDING =
  'This reply is already with the sending account. Sending it again would put two copies of it in front of the same person.'

/**
 * Whether Send in this card can reach the channel at all.
 *
 * The server's answer, derived the same way rather than listed here: the send
 * route refuses with a 409 unless the channel performs `send` (email) or `dm`
 * (everything else), read off the kernel's verb table. `METERED_ACTIONS_BY_CHANNEL`
 * is this app's mirror of that table, pinned to it by a build gate, so a
 * platform that gains a measured message verb gains a working Send here with no
 * edit in this file. Today the one channel without it is Facebook, whose
 * Message control opens Messenger's chat, so its threads are answered on the
 * phone and logged.
 */
export function consoleCanSend(channel: Channel): boolean {
  const verbs = (METERED_ACTIONS_BY_CHANNEL as Partial<Record<Channel, readonly ActionType[]>>)[channel] ?? []
  return verbs.includes(channel === 'email' ? 'send' : 'dm')
}

/**
 * The one line the card says on a channel Send cannot reach, or null.
 *
 * How the reply goes out, said where somebody is about to write it, rather than
 * a Send that answers 409 after they have. The way to record it once it has
 * gone is "Log as sent", under the arrow beside Send.
 */
export function consoleSendRefusal(channel: Channel): string | null {
  if (consoleCanSend(channel)) return null
  return `${label.channel(channel)} replies cannot be sent from here. Send it yourself, then log it as sent.`
}

/**
 * What one press of Send should do, given what the card is holding right now.
 *
 * Asked on every press rather than worked out once when the card opened, and
 * that is the point of it. The card starts with no draft, creates one on the
 * first press and keeps the id it was handed, so if the dispatcher refuses, the
 * second press sends the draft that now exists instead of writing a second one.
 * The failure this prevents is quiet: two drafts on a thread, one of them
 * orphaned, and nothing on screen that looks wrong.
 *
 * The two refusals mirror the server's own 409s deliberately. The server is
 * still the thing that decides, and it holds the race that this cannot see, but
 * a button that fires and then apologises reads as a platform that does not know
 * its own state.
 */
export function nextSendStep(input: {
  draftId: string | null
  state: ReplyDraftState | null
  body: string
  /**
   * The thread's channel. A channel Send cannot reach is refused before the
   * words are looked at, with the line that says how the reply goes out
   * instead. Left out, nothing is refused on the channel's account.
   */
  channel?: Channel
  /**
   * Something the card holds that the route would refuse, such as a copy that
   * is not an address (`copiesProblem`). Said on the button rather than after
   * the press.
   */
  problem?: string | null
}): SendStep {
  if (input.state === 'sent') return { kind: 'blocked', reason: ALREADY_SENT }
  if (input.state === 'sending') return { kind: 'blocked', reason: ALREADY_SENDING }
  const refusal = input.channel === undefined ? null : consoleSendRefusal(input.channel)
  if (refusal !== null) return { kind: 'blocked', reason: refusal }
  if (input.problem !== undefined && input.problem !== null) return { kind: 'blocked', reason: input.problem }
  if (input.body.trim() === '') return { kind: 'blocked', reason: NOTHING_WRITTEN }
  if (input.draftId === null) return { kind: 'create' }
  return { kind: 'send', draftId: input.draftId }
}

/**
 * What happened after a send, and whether the thread now shows it.
 *
 * The server sends its own sentence and it is used as it arrives rather than
 * being replaced here. A refusal or an outcome this build has no words for would
 * otherwise be described confidently and wrongly by the screen, which is the
 * failure mode the dispatcher's own copy exists to avoid.
 *
 * `settled` is what the screen acts on. It is true only when a real outbound
 * message was written onto the thread inside that call. A queued reply is not on
 * the thread, so the card stays open saying so instead of closing as though the
 * conversation had moved on.
 *
 * A REHEARSAL IS THE ONE EXCEPTION TO USING THE SERVER'S WORDS. When the
 * deployment runs dry the dispatcher answers `dispatchTarget: 'dry_run'`, and the
 * route as first built called that `sent` and said "The reply has gone out.",
 * which was false in every word. Nobody received anything. So a dry run is read
 * off the target rather than the state, drawn as a rehearsal, and leaves the
 * draft approved, whichever state and sentence came with it.
 */
export interface SendOutcome {
  settled: boolean
  /** True when the platform only rehearsed the send. Nothing reached anybody. */
  rehearsal: boolean
  summary: string
  /** The state to hold the draft in from here. */
  state: ReplyDraftState
}

export const DRY_RUN_TARGET = 'dry_run'

export const REHEARSAL_COPY =
  'This was a rehearsal. The platform is running dry, so nothing was sent to anyone and the draft is still here.'

export function sendOutcome(result: {
  state: ReplyDraftState
  summary: string
  dispatchTarget?: string | null
}): SendOutcome {
  if (result.dispatchTarget === DRY_RUN_TARGET) {
    return { settled: false, rehearsal: true, summary: REHEARSAL_COPY, state: 'approved' }
  }
  return {
    settled: result.state === 'sent',
    rehearsal: false,
    summary: result.summary,
    state: result.state,
  }
}

// ---------------------------------------------------------------------------
// The bell
// ---------------------------------------------------------------------------

/**
 * What a reminder is, said where somebody is setting one.
 *
 * NOTHING FIRES. There is no scheduler behind `remind_at`, no email, no badge
 * that lights up on the day. It is a date the thread carries and a tab that
 * filters on it. Every other product with a bell in it does send something, so
 * this sentence is not decoration: without it the operator's plan is "it will
 * tell me", and the thread goes unanswered on the day it mattered.
 *
 * "WITH THIS DATE ON IT", NOT "FROM THEN ON". The sentence used to say the
 * thread joins the Reminders tab from the date onward. The list route's
 * Reminders view is every thread whose `remind_at` is set at all, so it is on
 * the tab the moment the date is, and the row's bell says whether that date is
 * due or still ahead.
 */
export const REMINDER_NOTE =
  'Nothing will ping you. The thread sits on the Reminders tab with this date on it, and you look there.'

export const REMINDER_CLEARED = 'The reminder is off this thread.'

/** The moments most reminders are for, offered so nobody has to open a date picker. */
export const REMINDER_PRESETS: { days: number; label: string }[] = [
  { days: 1, label: 'Tomorrow' },
  { days: 3, label: 'In 3 days' },
  { days: 7, label: 'Next week' },
  { days: 30, label: 'In a month' },
]

/**
 * Whether a chosen moment can be asked for at all.
 *
 * The server refuses a moment that has already passed with a 409, and it is
 * right to: a reminder in the past would file the thread onto the Reminders tab
 * the instant it was set and never leave. Checked here so the refusal reads as a
 * date that needs changing rather than as a request that failed.
 */
export const REMINDER_IN_THE_PAST = 'Pick a time that has not happened yet.'

export function reminderRefusal(at: string | null, now: Date = new Date()): string | null {
  if (at === null) return REMINDER_IN_THE_PAST
  const parsed = new Date(at)
  if (Number.isNaN(parsed.getTime())) return REMINDER_IN_THE_PAST
  return parsed.getTime() <= now.getTime() ? REMINDER_IN_THE_PAST : null
}
